import React, { useState, useEffect } from 'react';
import SubHeader from '../components/SubHeader/SubHeader';
import Footer from '../components/Footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faClock,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import '../styles/DailyTicketReport.css';
import { getCheckedTickets } from '../services/authService';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

function DailyTicketReport() {
  const [searchParams] = useSearchParams();
  const fromDate = searchParams.get('fromDate');
  const toDate = searchParams.get('toDate');
  
  // Xác định timeFilter từ fromDate và toDate
  const determineTimeFilter = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 'year';

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const today = new Date();

    // Kiểm tra nếu là today
    if (from.toDateString() === to.toDateString() && 
        to.toDateString() === today.toDateString()) {
      return 'today';
    }

    // Kiểm tra nếu là month
    if (from.getDate() === 1 && 
        from.getMonth() === to.getMonth() &&
        to.toDateString() === today.toDateString()) {
      return 'month';
    }

    // Kiểm tra nếu là year
    if (from.getMonth() === 0 && from.getDate() === 1 &&
        to.toDateString() === today.toDateString()) {
      return 'year';
    }

    return 'year'; // Mặc định
  };

  const [timeFilter, setTimeFilter] = useState(() => 
    determineTimeFilter(fromDate, toDate)
  );

  useEffect(() => {
    setTimeFilter(determineTimeFilter(fromDate, toDate));
  }, [fromDate, toDate]);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL', 'CHECKIN', 'CHECKOUT'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Thêm state mới để lưu summary
  const [summary, setSummary] = useState({
    total_checkin: 0,
    total_checkout: 0,
    total_incomplete: 0
  });

  useEffect(() => {
    fetchTickets();
  }, [timeFilter]);

  const getDateRange = () => {
    // Tạo ngày hiện tại theo GMT+7
    const today = new Date();
    today.setHours(today.getHours() + 7); // Chuyển sang GMT+7
    
    const fromDate = new Date(today);
    fromDate.setHours(fromDate.getHours() + 7); // Chuyển sang GMT+7
    
    switch(timeFilter) {
      case 'week':
        // Lấy ngày đầu tuần (thứ 2)
        const day = fromDate.getDay();
        const diff = fromDate.getDate() - day + (day === 0 ? -6 : 1);
        fromDate.setDate(diff);
        break;
      case 'month':
        fromDate.setDate(1);
        break;
      case 'year':
        fromDate.setMonth(0, 1);
        break;
      case 'custom':
        return {
          fromDate: customFromDate,
          toDate: customToDate
        };
      default: // today
        break;
    }

    return {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const { fromDate, toDate } = getDateRange();
      const data = await getCheckedTickets(fromDate, toDate);
      setTickets(data.tickets || []);
      // Lưu summary từ API
      setSummary({
        total_checkin: data.summary?.total_checkin || 0,
        total_checkout: data.summary?.total_checkout || 0,
        total_incomplete: data.summary?.total_incomplete || 0
      });
    } catch (error) {
      console.error('Lỗi khi lấy danh sách vé:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'CHECKIN':
        return 'Chưa ra';
      case 'CHECKOUT':
        return 'Đã ra';
      default:
        return status;
    }
  };

  // Hàm chuyển đổi string date từ API sang Date object
  const parseVietnameseDate = (dateStr) => {
    if (!dateStr) return null;
    // Tách thời gian và ngày
    const [time, date] = dateStr.split(' ');
    // Tách ngày tháng năm
    const [day, month, year] = date.split('/');
    // Tạo Date object (month - 1 vì trong JS tháng bắt đầu từ 0)
    return new Date(year, month - 1, day);
  };

  // Sửa lại hàm formatTime
  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const [time] = dateTimeStr.split(' ');
    // Lấy giờ và phút
    const [hours, minutes] = time.split(':');
    return `${hours}:${minutes}`;
  };

  // Sửa lại hàm formatDate
  const formatDate = (dateStr) => {
    const date = parseVietnameseDate(dateStr);
    if (!date) return '';
    
    return date.toLocaleDateString('vi-VN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getFilteredTickets = () => {
    if (statusFilter === 'ALL') return tickets;
    return tickets.filter(ticket => ticket.status === statusFilter);
  };

  const handleStatusClick = (status) => {
    setStatusFilter(status === statusFilter ? 'ALL' : status);
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  };

  const handleDateChange = () => {
    if (customFromDate && customToDate) {
      fetchTickets();
    }
  };

  // Hàm để nhóm vé theo ngày
  const groupTicketsByDate = (tickets) => {
    const groups = {};
    
    tickets.forEach(ticket => {
      if (!ticket.checkin_at) return;
      
      // Lấy ngày từ chuỗi checkin_at
      const [_, date] = ticket.checkin_at.split(' ');
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(ticket);
    });
    
    // Sắp xếp theo ngày mới nhất
    return Object.entries(groups)
      .sort(([dateA], [dateB]) => {
        const dateObjA = parseVietnameseDate(`00:00:00 ${dateA}`);
        const dateObjB = parseVietnameseDate(`00:00:00 ${dateB}`);
        return dateObjB - dateObjA;
      });
  };

  return (
    <div className="dtr-container">
      <SubHeader title="Danh sách vé" />
      
      <main className="dtr-main">
        {/* Fixed section */}
        <div className="dtr-fixed-section">
          {/* Dropdown filter */}
          <div className="dtr-filter">
            <div className="dtr-filter-select">
              <FontAwesomeIcon icon={faCalendarAlt} className="dtr-filter-icon" />
              <select 
                value={timeFilter}
                onChange={(e) => handleTimeFilterChange(e.target.value)}
                className="dtr-select"
              >
                <option value="today">Hôm nay</option>
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
                <option value="year">Năm nay</option>
                <option value="custom">Tùy chọn thời gian</option>
              </select>
            </div>

            {showDatePicker && (
              <div className="dtr-date-picker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update);
                    if (update[0] && update[1]) {
                      setCustomFromDate(update[0].toISOString().split('T')[0]);
                      setCustomToDate(update[1].toISOString().split('T')[0]);
                    }
                  }}
                  isClearable={true}
                  placeholderText="Từ ngày - Đến ngày"
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  dropdownMode="select"
                />
                <button 
                  className="dtr-date-apply"
                  onClick={handleDateChange}
                  disabled={!startDate || !endDate}
                >
                  Áp dụng
                </button>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="dtr-summary">
            <div className="dtr-summary-card">
              <div className="dtr-stats">
                <div className="dtr-stat-item dtr-total" onClick={() => handleStatusClick('ALL')}>
                  <span className={`dtr-stat-value ${statusFilter === 'ALL' ? 'active' : ''}`}>
                    {summary.total_checkin}
                  </span>
                  <span className="dtr-stat-label">Lượt vào</span>
                </div>
                <div 
                  className="dtr-stat-item dtr-completed" 
                  onClick={() => handleStatusClick('CHECKOUT')}
                >
                  <span className={`dtr-stat-value ${statusFilter === 'CHECKOUT' ? 'active' : ''}`}>
                    {summary.total_checkout}
                  </span>
                  <span className="dtr-stat-label">Lượt ra</span>
                </div>
                <div 
                  className="dtr-stat-item dtr-pending"
                  onClick={() => handleStatusClick('CHECKIN')}
                >
                  <span className={`dtr-stat-value ${statusFilter === 'CHECKIN' ? 'active' : ''}`}>
                    {summary.total_incomplete}
                  </span>
                  <span className="dtr-stat-label">Chưa ra</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable list */}
        <div className="dtr-list-container">
          <div className="dtr-list">
            {loading ? (
              <div className="dtr-loading">Đang tải...</div>
            ) : (
              groupTicketsByDate(getFilteredTickets()).map(([date, tickets]) => (
                <div key={date} className="dtr-date-group">
                  <div className="dtr-date-header">
                    <span>{formatDate(`00:00:00 ${date}`)}</span>
                    <span className="dtr-date-count">&nbsp;</span>
                  </div>
                  {tickets.map(ticket => (
                    <div key={ticket.id} className={`dtr-item ${ticket.status.toLowerCase()}`}>
                      <div className="dtr-item-left">
                        <div className="dtr-item-header">
                          <span className="dtr-item-code">#{ticket.code}</span>
                          <span className="dtr-item-type">{ticket.name}</span>
                        </div>
                        {ticket.commission && (
                          <div className="dtr-item-commission">
                            {new Intl.NumberFormat('vi-VN', { 
                              style: 'currency', 
                              currency: 'VND' 
                            }).format(ticket.commission)}
                          </div>
                        )}
                      </div>
                      <div className="dtr-item-right">
                        <div className={`dtr-item-status ${ticket.status.toLowerCase()}`}>
                          <FontAwesomeIcon icon={ticket.status === 'CHECKOUT' ? faCheckCircle : faClock} />
                          <span>{getStatusText(ticket.status)}</span>
                          {ticket.is_checkout_with_other == 1 && (
                            <span className="dtr-item-one-way">Vé 1 chiều</span>
                          )}
                          {ticket.paid && (
                            <span className="dtr-item-paid">Đã thanh toán</span>
                          )}
                        </div>
                        <div className="dtr-item-times">
                          <div className="dtr-time">
                            Vào: {formatTime(ticket.checkin_at)}
                            {ticket.checkout_at && ` • Ra: ${formatTime(ticket.checkout_at)}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default DailyTicketReport; 